import {
	BOX_SHADOWS,
	CLASSIFICATION_MODELS,
	DATA_TYPES,
	FONTS,
	MODEL_STATES,
	PRIMARY_COLOR,
	REGRESSSION_MODELS,
	SECONDARY_COLOR,
} from "../../constants";

import { Button } from "@material-ui/core";
import Checkbox from "@material-ui/core/Checkbox";
import Paper from "@material-ui/core/Paper";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import React from "react";
import Column from "../../MachineLearning/Column";
import startTrainFlow from "../../MachineLearning/trainer";
import { stateType, stateTypeF } from "../../state";
import { BoxHeader2 } from "../Common/BoxHeader";
import { HelpIcon } from "../Common/ToolTipIcon";
import ProgressBar from "./ProgressBar";

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		container: {
			display: "flex",
			flexDirection: "column",
			width: "100%",
			backgroundColor: "white",
			boxShadow: BOX_SHADOWS,
			borderRadius: "10px",
			height: "100%",
		},
		root: {
			width: "100%",
		},
		paper: {
			width: "100%",
			marginBottom: theme.spacing(2),
		},
		table: {
			minWidth: 750,
		},
		visuallyHidden: {
			border: 0,
			clip: "rect(0 0 0 0)",
			height: 1,
			margin: -1,
			overflow: "hidden",
			padding: 0,
			position: "absolute",
			top: 20,
			width: 1,
		},
		trainButton: {},
		disabledButton: {
			"&:disabled": {
				backgroundColor: "red",
			},
		},
		fastTraining: {
			fontWeight: "normal",
			display: "flex",
			marginLeft: "30px",
			flexDirection: "row",
			width: "0px",
			lineHeight: "22px",
		},
	})
);

interface Data {
	calories: number;
	carbs: number;
	fat: string;
	name: string;
	protein: number;
}

function createData(
	name: string,
	calories: number,
	fat: string,
	carbs: number,
	protein: number
): Data {
	return { name, calories, fat, carbs, protein };
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
	if (b[orderBy] < a[orderBy]) {
		return -1;
	}
	if (b[orderBy] > a[orderBy]) {
		return 1;
	}
	return 0;
}

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(
	order: Order,
	orderBy: Key
): (
	a: { [key in Key]: number | string },
	b: { [key in Key]: number | string }
) => number {
	return order === "desc"
		? (a, b) => descendingComparator(a, b, orderBy)
		: (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
	const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
	stabilizedThis.sort((a, b) => {
		const order = comparator(a[0], b[0]);
		if (order !== 0) return order;
		return a[1] - b[1];
	});
	return stabilizedThis.map((el) => el[0]);
}

interface HeadCell {
	disablePadding: boolean;
	id: keyof Data;
	label: string;
	numeric: boolean;
}

const headCells: HeadCell[] = [
	{
		id: "name",
		numeric: false,
		disablePadding: true,
		label: "Model Name",
	},
	{
		id: "calories",
		numeric: false,
		disablePadding: false,
		label: "Training Progress",
	},
	{ id: "fat", numeric: false, disablePadding: false, label: "Status" },
];

interface EnhancedTableProps {
	classes: ReturnType<typeof useStyles>;
	selected: string[];
	models: string[];
	onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
	order: Order;
	orderBy: string;
}

function EnhancedTableHead(props: EnhancedTableProps) {
	const { classes, onSelectAllClick, order, orderBy, selected, models } =
		props;

	let rowCount = models.length;
	let numSelected = models.filter((v) => selected.includes(v)).length;
	return (
		<TableHead>
			<TableRow>
				<TableCell padding="checkbox">
					<Checkbox
						style={{ color: SECONDARY_COLOR }}
						indeterminate={
							numSelected > 0 && numSelected < rowCount
						}
						checked={rowCount > 0 && numSelected === rowCount}
						onChange={onSelectAllClick}
						inputProps={{ "aria-label": "select all desserts" }}
					/>
				</TableCell>
				{headCells.map((headCell) => (
					<TableCell
						key={headCell.id}
						align={headCell.id === "fat" ? "center" : "left"}
						padding={headCell.disablePadding ? "none" : "normal"}
						style={{ fontFamily: FONTS, fontWeight: "bold" }}
					>
						{headCell.label}
					</TableCell>
				))}
			</TableRow>
		</TableHead>
	);
}

function getModelStatus(state: stateTypeF, modelName: string) {
	let modelStates = state.saveState.modelTraining.modelStates;
	const modelState = modelStates[modelName];

	let trainStatus = "Ready to Train";

	switch (modelState) {
		case MODEL_STATES.trained:
			trainStatus = "Trained";
			break;
		case MODEL_STATES.training:
			trainStatus = "Training...";
			break;
		case MODEL_STATES.premOnly:
			trainStatus = "Premium Model";
			break;
		case MODEL_STATES.unselected:
			trainStatus = "Disabled";
	}
	return trainStatus;
}

function getModels(col: Column) {
	return col.columnType === DATA_TYPES.categorical
		? CLASSIFICATION_MODELS
		: REGRESSSION_MODELS;
}

function getSelected(state, models) {
	let selected: string[] = Object.values(models).filter(
		(v: string) =>
			state.saveState.modelTraining.modelStates[v] !==
			MODEL_STATES.unselected
	) as string[];
	return selected;
}

export default function EnhancedTable({ state }: stateType) {
	const classes = useStyles();
	const [order, setOrder] = React.useState<Order>("asc");
	const [orderBy, setOrderBy] = React.useState<keyof Data>("calories");
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(6);

	const valToPredict = state.saveState.modelTraining.valueToPredict;
	const col = state.saveState.dataset.getColumn(valToPredict);
	if (col === undefined) {
		return;
	}
	const models = getModels(col);
	const rows = Object.values(models).map((m) =>
		createData(m, 305, getModelStatus(state, m), 67, 4.3)
	);

	let selected = getSelected(state, models);

	function handleSelectedClick(modelName: string) {
		let modelStates = state.saveState.modelTraining.modelStates;
		let modelState = modelStates[modelName];

		if (state.isBusy) {
			return;
		}
		if (modelState === MODEL_STATES.unselected) {
			const isSaved =
				state.saveState.savedModels.filter(
					(model) => model.modelName === modelName
				).length > 0;
			var newState = MODEL_STATES.ready;
			if (isSaved) {
				newState = MODEL_STATES.trained;
			}
		} else {
			var newState = MODEL_STATES.unselected;
		}
		state.setModelState(modelName, newState, modelStates);
	}

	const handleSelectAllClick = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		if (event.target.checked) {
			for (let v of Object.values(models)) {
				state.setModelState(
					v,
					MODEL_STATES.ready,
					state.saveState.modelTraining.modelStates
				);
			}
			const newSelecteds = rows.map((n) => n.name);
			return;
		}
		for (let v of Object.values(models)) {
			state.setModelState(
				v,
				MODEL_STATES.unselected,
				state.saveState.modelTraining.modelStates
			);
		}
	};

	const isSelected = (name: string) => selected.indexOf(name) !== -1;

	return (
		<div className={classes.root}>
			<Paper className={classes.paper} elevation={0}>
				<TableContainer>
					<Table
						className={classes.table}
						aria-labelledby="tableTitle"
						size={"small"}
						aria-label="enhanced table"
					>
						<EnhancedTableHead
							classes={classes}
							selected={selected}
							models={Object.values(models)}
							order={order}
							orderBy={orderBy}
							onSelectAllClick={handleSelectAllClick}
						/>
						<TableBody>
							{stableSort(rows, getComparator(order, orderBy))
								.slice(
									page * rowsPerPage,
									page * rowsPerPage + rowsPerPage
								)
								.map((row, index) => {
									const isItemSelected = isSelected(
										row.name as string
									);
									const labelId = `enhanced-table-checkbox-${index}`;

									return (
										<TableRow
											hover
											onClick={(event) =>
												handleSelectedClick(
													row.name as string
												)
											}
											role="checkbox"
											aria-checked={isItemSelected}
											tabIndex={-1}
											key={row.name}
										>
											<TableCell padding="checkbox">
												<Checkbox
													checked={
														state.saveState
															.modelTraining
															.modelStates[
															row.name
														] !==
														MODEL_STATES.unselected
													}
													inputProps={{
														"aria-labelledby":
															labelId,
													}}
													style={{
														color: PRIMARY_COLOR,
													}}
												/>
											</TableCell>
											<TableCell
												component="th"
												id={labelId}
												scope="row"
												padding="none"
												style={{
													fontFamily: FONTS,
													width: "300px",
												}}
											>
												{row.name}
											</TableCell>
											<TableCell align="right">
												{
													<ProgressBar
														progress={
															state.saveState
																.modelTraining
																.modelProgress[
																row.name
															]
														}
														isDisabled={
															state.saveState
																.modelTraining
																.modelStates[
																row.name
															] ===
															MODEL_STATES.unselected
														}
													></ProgressBar>
												}
											</TableCell>
											<TableCell
												align="center"
												style={{
													fontFamily: FONTS,
													width: "200px",
													justifyContent: "center",
												}}
											>
												<div
													style={{
														marginLeft: "5px",
													}}
												>
													{row.fat}
												</div>
											</TableCell>
										</TableRow>
									);
								})}
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>
		</div>
	);
}

export function FastTrianingCheck({ state }: stateType) {
	const classes = useStyles();
	return (
		<div className={classes.fastTraining}>
			<Checkbox
				checked={state.saveState.modelTraining.isFastTrainingSelected}
				onChange={() => {
					state.saveState.modelTraining.isFastTrainingSelected =
						!state.saveState.modelTraining.isFastTrainingSelected;
					state.setSaveState(state.saveState);
				}}
				inputProps={{ "aria-label": "primary checkbox" }}
				style={{
					height: "0px",
					marginTop: "2px",
					color: SECONDARY_COLOR,
				}}
			/>
			<div style={{ marginLeft: "-3px", fontFamily: FONTS }}>
				Fast Training
			</div>
			<HelpIcon
				title={
					"For large datasets, you can optionally train smaller ML models for quicker results at the potential cost of some accuracy."
				}
				marginLeft={"8px"}
			></HelpIcon>
		</div>
	);
}

export function ModelTrainingBox({ state }: stateType) {
	const classes = useStyles();

	function handelCancel() {
		if (state.isTraining || state.isProcessingData) {
			state.setTrainingCanceled(true);
			state.resetModelProgress();
		}
	}

	const valToPredict = state.saveState.modelTraining.valueToPredict;
	const col = state.saveState.dataset.getColumn(valToPredict);
	if (col === undefined) {
		return <div></div>;
	}

	const models = getModels(col);
	let selected = getSelected(state, models);
	let isDisabled =
		state.isTraining ||
		state.isProcessingData ||
		state.isAnalyzing ||
		selected.length === 0;
	return (
		<div className={classes.container}>
			<BoxHeader2
				state={state}
				title={"Machine Learning Models"}
				toolTip={`Train multiple Machine Learning Models to work together and produce a single, powerful, predictive model.`}
			></BoxHeader2>

			<EnhancedTable state={state}></EnhancedTable>

			<div
				style={{
					display: "flex",
					flexDirection: "row",
				}}
			>
				<div
					style={{
						marginLeft: "20px",
						marginTop: "00px",
						fontFamily: FONTS,
						fontWeight: "bold",
					}}
				>
					{
						<div style={{ display: "flex", flexDirection: "row" }}>
							{" "}
							Total Training Progress:{" "}
							<span
								style={{
									color: SECONDARY_COLOR,
									marginLeft: "5px",
								}}
							>
								{Math.round(
									state.saveState.modelTraining
										.totalTrainProgress
								)}
								%
							</span>
						</div>
					}
				</div>
				<div
					style={{
						marginTop: "0px",
						marginLeft: "auto",
						marginRight: "20px",
						marginBottom: "20px",
					}}
				>
					<Button
						variant="contained"
						color="primary"
						onClick={() => startTrainFlow(state)}
						style={{
							fontFamily: FONTS,
							fontWeight: "bold",
							width: "160px",
							height: "40px",
							backgroundColor: SECONDARY_COLOR,
							color: isDisabled
								? "rgba(255,255,255,0.6)"
								: "white",
						}}
						disabled={isDisabled}
						className={classes.trainButton}
					>
						{state.isTraining
							? "Training..."
							: state.isProcessingData
							? "Processing..."
							: state.isAnalyzing
							? "Analyzing..."
							: "Train Models"}
					</Button>
					<Button
						variant="contained"
						color="primary"
						style={{
							backgroundColor:
								state.isTraining ||
								state.isProcessingData ||
								state.isAnalyzing
									? "rgba(0,0,0,.2)"
									: "rgba(0,0,0,.05)",
							fontFamily: FONTS,
							fontWeight: "bold",
							height: "40px",
							marginLeft: "20px",
						}}
						size="large"
						disabled={!state.isTraining && !state.isProcessingData}
						onClick={handelCancel}
						className={classes.disabledButton}
					>
						Cancel
					</Button>
				</div>
			</div>
		</div>
	);
}
